<script setup lang="ts">
import { computed } from 'vue';
import { buildSimpleWhatsappUrl } from '~/utils/booking-message';

interface Apartment {
  listing_id: number;
  title: string;
  cover_photo: { url: string; alt?: string };
  capacity?: number | string;
  rooms?: number | string;
  area_sqm?: number;
  view_type?: 'sea' | 'panoramic' | 'mountain';
  floor?: number | string;
  discount?: string;
}

const props = withDefaults(
  defineProps<{
    apartment: Apartment;
    gallery?: Array<{ url: string; alt?: string }>;
    catalogPrice?: number | null;
    locale: string;
    whatsappNumber?: string;
    telegramLink?: string;
    guestFacts?: { capacity: number; threshold: number; extraGuestUsd?: number } | null;
  }>(),
  {
    gallery: () => [],
    catalogPrice: null,
    whatsappNumber: '',
    telegramLink: '',
    guestFacts: null,
  },
);

const hasGallery = computed(() => Array.isArray(props.gallery) && props.gallery.length >= 2);

const { t } = useI18n();
const route = useRoute();
const localePath = useLocalePath();
const { trackEvent } = useAnalytics();

const {
  baseGuests,
  extraGuests,
  totalGuests,
  extraGuestUsd,
  roomsNum,
  floorNum,
  isHighFloor,
  viewLabel,
  areaSqmRounded,
} = useApartmentFacts(
  () => props.apartment,
  () => props.guestFacts,
);

const extraGuestTitle = computed(() =>
  extraGuestUsd.value
    ? t('apartmentRow.facts.extraGuestHintPriced', { price: extraGuestUsd.value })
    : t('apartmentRow.facts.extraGuestHint'),
);

// Preserve booking context (dates + guests) when navigating from any page that
// carries them in the URL — the home hero mirrors BookingSearchForm state into
// `?from=&to=&guests=` for exactly this reason. Mirrors ApartmentRow.vue.
const detailsPath = computed(() => {
  const path = localePath(`/apartment/${props.apartment.listing_id}`);
  const q: Record<string, string> = {};
  if (typeof route.query.from === 'string' && route.query.from) q.from = route.query.from;
  if (typeof route.query.to === 'string' && route.query.to) q.to = route.query.to;
  const g = route.query.guests;
  if (g && Number(g) > 0) q.guests = String(g);
  return Object.keys(q).length ? { path, query: q } : path;
});

const whatsappUrl = computed(() =>
  props.whatsappNumber
    ? buildSimpleWhatsappUrl(props.whatsappNumber, {
        apartmentTitle: props.apartment.title,
        listingId: props.apartment.listing_id,
        locale: props.locale,
      })
    : '',
);
</script>

<template>
  <div
    class="group flex flex-col h-full bg-white dark:bg-slate-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden dark:border dark:border-slate-700"
  >
    <NuxtLink
      :to="detailsPath"
      :aria-label="t('apartmentList.detailsAria', { title: apartment.title })"
      class="relative block overflow-hidden aspect-[4/3]"
    >
      <ApartmentCardSwiper
        v-if="hasGallery"
        :images="gallery"
        :title="apartment.title"
        class="md:hidden absolute inset-0"
      />
      <NuxtImg
        :src="apartment.cover_photo.url"
        :alt="apartment.cover_photo.alt ?? apartment.title"
        width="400"
        height="300"
        format="webp"
        loading="lazy"
        sizes="xs:100vw sm:50vw md:50vw lg:25vw"
        :class="hasGallery
          ? 'hidden md:block w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700'
          : 'w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700'"
      />
      <div
        class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
      />
      <div v-if="apartment.discount" class="absolute top-4 start-4 pointer-events-none">
        <span
          class="bg-red-500 text-white text-[12px] px-3 py-1 font-bold uppercase tracking-widest rounded-sm shadow-lg"
        >
          {{ apartment.discount }}
        </span>
      </div>
    </NuxtLink>

    <div class="p-6 flex-1 flex flex-col">
      <!-- Badges: view + high-floor -->
      <div
        v-if="viewLabel || isHighFloor"
        class="flex flex-wrap items-center gap-1.5 mb-3"
      >
        <span
          v-if="viewLabel"
          class="inline-flex items-center rounded-full bg-amber-100 px-2 font-body text-[10px] font-semibold uppercase tracking-[0.14em] leading-[18px] text-amber-800 dark:bg-amber-500/15 dark:text-amber-200"
        >
          {{ viewLabel }}
        </span>
        <span
          v-if="isHighFloor && floorNum"
          class="inline-flex items-center rounded-full bg-slate-100 px-2 font-body text-[10px] font-semibold uppercase tracking-[0.14em] leading-[18px] text-slate-700 dark:bg-slate-700/60 dark:text-slate-200"
        >
          {{ t('apartmentRow.badges.floor', { floor: floorNum }) }}
        </span>
      </div>

      <NuxtLink
        :to="detailsPath"
        class="text-xl font-display font-medium text-slate-900 dark:text-white hover:text-red-500 transition-colors duration-300 mb-3 line-clamp-2"
      >
        {{ apartment.title }}
      </NuxtLink>

      <!-- Facts: guests · rooms · area -->
      <div
        v-if="baseGuests || roomsNum || areaSqmRounded"
        class="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-4 font-body text-[13px] leading-tight text-slate-500 dark:text-slate-400"
      >
        <span v-if="baseGuests" class="inline-flex items-center gap-1">
          <Icon name="lucide:users" class="size-3 text-slate-400" aria-hidden="true" />
          <span>{{ baseGuests }}</span>
          <span
            v-if="extraGuests > 0"
            :title="extraGuestTitle"
            class="inline-flex items-center rounded bg-amber-100/70 px-1 font-semibold tabular-nums text-amber-700 cursor-help dark:bg-amber-400/15 dark:text-amber-300"
          >+{{ extraGuests }}</span>
          <span>{{ t('apartmentRow.facts.guestsWord', totalGuests ?? baseGuests) }}</span>
        </span>
        <span v-if="baseGuests && (roomsNum || areaSqmRounded)" aria-hidden="true" class="text-amber-500/60">·</span>
        <span v-if="roomsNum" class="inline-flex items-center gap-1">
          <Icon name="lucide:bed-single" class="size-3 text-slate-400" aria-hidden="true" />
          {{ t('apartmentRow.facts.rooms', { count: roomsNum }, roomsNum) }}
        </span>
        <span v-if="roomsNum && areaSqmRounded" aria-hidden="true" class="text-amber-500/60">·</span>
        <span v-if="areaSqmRounded" class="inline-flex items-center gap-1">
          <Icon name="lucide:ruler" class="size-3 text-slate-400" aria-hidden="true" />
          {{ t('apartmentRow.facts.area', { value: areaSqmRounded }) }}
        </span>
      </div>

      <div
        class="mt-auto pt-4 flex justify-between items-center border-t border-slate-100 dark:border-slate-700"
      >
        <div class="flex items-baseline gap-1.5">
          <span class="text-xs text-slate-400">
            {{ t('apartmentList.price.startFrom') }}
          </span>
          <span
            v-if="catalogPrice != null"
            class="text-xl font-display italic text-red-500 leading-none"
          >
            ${{ catalogPrice }}
            <span class="text-sm font-sans not-italic text-slate-400">{{
              t('apartmentList.price.perNight')
            }}</span>
          </span>
        </div>

        <div class="flex items-center gap-2">
          <a
            v-if="telegramLink"
            :href="telegramLink"
            :aria-label="t('apartmentList.telegramAria', { title: apartment.title })"
            target="_blank"
            rel="noopener"
            class="size-10 flex items-center justify-center rounded-full bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300 hover:bg-sky-500 hover:text-white transition-all duration-300 shadow-sm"
            @click="trackEvent('card_telegram_click', { listing_id: apartment.listing_id, locale })"
          >
            <Icon name="ic:baseline-telegram" class="text-lg" />
          </a>
          <a
            v-if="whatsappNumber"
            :href="whatsappUrl"
            :aria-label="t('apartmentList.whatsappAria', { title: apartment.title })"
            target="_blank"
            rel="noopener"
            class="size-10 flex items-center justify-center rounded-full bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-500 hover:text-white transition-all duration-300 shadow-sm"
            @click="trackEvent('card_whatsapp_click', { listing_id: apartment.listing_id, locale })"
          >
            <Icon name="mdi-whatsapp" class="text-lg" />
          </a>
          <NuxtLink
            :to="detailsPath"
            :aria-label="t('apartmentList.detailsAria', { title: apartment.title })"
            class="size-10 flex items-center justify-center rounded-full bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white group-hover:bg-red-500 group-hover:text-white transition-all duration-300 shadow-sm"
          >
            <Icon name="mdi-arrow-right" class="text-lg" />
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>
